import { Injectable, Inject } from '@angular/core';
import { DA_SERVICE_TOKEN, ITokenService, JWTTokenModel } from '@delon/auth';
import { _HttpClient, SettingsService } from '@delon/theme';
import { locale, loadMessages } from 'devextreme/localization';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from 'src/app/core/util/auth.service';

@Injectable({
  providedIn: 'root'
})
export class DateFormatService {
  // Date format constants - single source of truth
  private readonly DATE_FORMATS = {
    PH: 'MM/dd/yyyy',
    DAY_FIRST: 'dd/MM/yyyy',
    DEFAULT: 'dd/MM/yyyy'
  };

  // Countries using day-first format (dd/MM/yyyy)
  private readonly DAY_FIRST_COUNTRIES = new Set([
    'MY', // Malaysia
    'SG', // Singapore
    'ID', // Indonesia
    'TH', // Thailand
    'VN', // Vietnam
    'KH', // Cambodia
    'MM', // Myanmar
    'BN', // Brunei
    'LA', // Laos
    'TL' // Timor-Leste
  ]);

  get country() {
    // Try multiple ways to get country
    const company = this.settings.user['company'];
    if (company?.country) {
      return company.country;
    }

    // Fallback: try getting from user directly (sometimes stored there)
    if (this.settings.user['country']) {
      return this.settings.user['country'];
    }

    return undefined;
  }

  get dateFormat() {
    return this.settings.user['dateFormat'];
  }

  private dateFormatSubject = new BehaviorSubject<string>(
    this.getDefaultDateFormat()
  ); // default format

  private dateTimeFormatSubject = new BehaviorSubject<string>(
    this.getDefaultDateTimeFormat()
  );

  private currentTenantId?: string;

  constructor(
    private http: _HttpClient,
    private settings: SettingsService,
    private auth: AuthService,
    @Inject(DA_SERVICE_TOKEN) private tokenService: ITokenService
  ) {
    this.auth.tokenChanged.on(token => {
      const nextTenantId =
        token?.['tenantId'] ?? this.settings.user?.['company']?.['tenantId'];

      if (!nextTenantId || nextTenantId === this.currentTenantId) return;

      this.currentTenantId = nextTenantId;
      this.resetDateFormats();
      this.loadTenantDateFormat();
    });
  }

  getDefaultDateFormat(): string {
    // Default date format based on the company country
    if (this.country === 'PH') return this.DATE_FORMATS.PH;
    if (this.country && this.DAY_FIRST_COUNTRIES.has(this.country))
      return this.DATE_FORMATS.DAY_FIRST;
    return this.DATE_FORMATS.DEFAULT; // Default for other countries
  }

  getDefaultDateTimeFormat(): string {
    //If default date format has value
    // if(this.dateFormat)
    //   return this.dateFormat;

    // Default date format based on the company country
    if (this.country === 'PH') return 'MM/dd/yyyy hh:mm:ss a';
    if (this.country && this.DAY_FIRST_COUNTRIES.has(this.country))
      return 'dd/MM/yyyy hh:mm:ss a';
    return 'dd/MM/yyyy hh:mm:ss a'; // Default for other countries
  }

  getExcelDateFormat(): string {
    return this.toExcelDateFormat(this.currentDateFormatValue);
  }

  getExcelDateTimeFormat(): string {
    return this.toExcelDateFormat(this.currentDateTimeFormatValue);
  }

  private toExcelDateFormat(format: string): string {
    if (!format) return 'mm/dd/yyyy';

    return format.replace(/yyyy|MMM|MM|dd|hh|mm|ss|a/g, match => {
      switch (match) {
        case 'yyyy':
          return 'yyyy';
        case 'MMM':
          return 'mmm';
        case 'MM':
          return 'mm';
        case 'dd':
          return 'dd';
        case 'hh':
          return 'hh';
        case 'mm':
          return 'mm';
        case 'ss':
          return 'ss';
        case 'a':
          return 'AM/PM';
        default:
          return match;
      }
    });
  }

  private resetDateFormats(): void {
    // Clear first, then repopulate with the new company defaults
    this.dateFormatSubject.next('');
    this.dateTimeFormatSubject.next('');
    this.dateFormatSubject.next(this.getDefaultDateFormat());
    this.dateTimeFormatSubject.next(this.getDefaultDateTimeFormat());
  }

  setDateFormat(format: string): void {
    this.http.post('api/tenantdata', { date_format: format }).subscribe({
      next: () => {
        const currentUser = this.settings.user || {};
        this.settings.setUser({ ...currentUser, ['dateFormat']: format });
        this.dateFormatSubject.next(format);
        this.dateTimeFormatSubject.next(
          this.getDateTimeFormatByDateFormat(format)
        );
      },
      error: error => {
        console.error('Error updating date format:', error);
      }
    });
  }

  getDateFormat(): Observable<string> {
    const dateFormat =
      this.currentDateFormatValue || this.getDefaultDateFormat();
    this.dateFormatSubject.next(dateFormat);
    return this.dateFormatSubject.asObservable();
  }

  get currentDateFormat(): Observable<string> {
    return this.dateFormatSubject.asObservable();
  }

  get currentDateFormatValue(): string {
    return this.dateFormatSubject.value;
  }

  get currentDateTimeFormatValue(): string {
    return this.dateTimeFormatSubject.value;
  }

  /**
   * Configure DevExpress locale to match company's date format
   * This sets the global locale for all DevExpress date pickers/filters
   */
  configureDevExpressLocale(): void {
    const country = this.country;

    // Define custom locale messages for date formatting using our constants
    const customMessages = {
      'en-MY': {
        'dxDateBox-dateFormat': this.DATE_FORMATS.DAY_FIRST
      },
      'en-PH': {
        'dxDateBox-dateFormat': this.DATE_FORMATS.PH
      }
    };

    // Load custom messages
    loadMessages(customMessages);

    // Set locale based on country
    if (country === 'PH') {
      locale('en-PH');
      return;
    }

    if (country && this.DAY_FIRST_COUNTRIES.has(country)) {
      locale('en-MY');
      return;
    }

    locale('en'); // Default English locale
  }

  /**
   * Load tenant date format from API. Call after app started and user is authorized
   * (either with existing token or after login). Used by StartupService.
   */
  loadTenantDateFormat(): void {
    const token = this.tokenService.get<JWTTokenModel>(JWTTokenModel);

    // If there is no valid token, return
    if (token == null || !token.token) {
      return;
    }

    this.http
      .get<Record<string, string>>('api/tenantdata', {
        keys: 'date_format',
        _silent: true
      })
      .subscribe({
        next: (res: Record<string, string>) => {
          const format = res?.['date_format'];
          const currentUser = this.settings.user || {};

          if (format) {
            this.settings.setUser({ ...currentUser, ['dateFormat']: format });
            this.dateFormatSubject.next(format);
            this.dateTimeFormatSubject.next(
              this.getDateTimeFormatByDateFormat(format)
            );
            return;
          }

          const defaultFormat = this.getDefaultDateFormat();
          this.settings.setUser({ ...currentUser, ['dateFormat']: undefined });
          this.dateFormatSubject.next(defaultFormat);
          this.dateTimeFormatSubject.next(
            this.getDateTimeFormatByDateFormat(defaultFormat)
          );
        },
        error: () => {
          const defaultFormat = this.getDefaultDateFormat();
          this.dateFormatSubject.next(defaultFormat);
          this.dateTimeFormatSubject.next(
            this.getDateTimeFormatByDateFormat(defaultFormat)
          );
        }
      });
  }

  private getDateTimeFormatByDateFormat(dateFormat: string): string {
    if (!dateFormat) return this.getDefaultDateTimeFormat();
    return `${dateFormat} hh:mm:ss a`;
  }
}
